import { tick } from './haptic'

// Hold a card, then drag it onto a day, on a touch screen (Liam's phone, 9 Oct 2026, To place: "Dragging a customer
// tile isnt easy or satisfying and doesnt look great"). The phone's own drag showed a grey copy of the card in its
// old look and was easy to lose. Here: hold the card (a quarter of a second, without moving), it lifts under the
// finger as itself with a light tap you feel, the days it can go to show, the one under the finger turns lemon, and
// letting go there places it exactly as tapping the card and then the day does. Letting go anywhere else puts it back.
// A quick swipe before the hold scrolls the row as before; a plain tap still picks the card.

export interface HoldDragOptions {
  // The day under the finger that can take this card, or null.
  dayAt: (x: number, y: number) => { el: HTMLElement; iso: string } | null
  // The days that can take it, shown while it is held.
  days: () => HTMLElement[]
  // Put it on that day.
  drop: (iso: string) => void
  // A control inside the card a hold may still start on, for a card that is mostly its own button (the Diary's
  // "Dates offered, waiting" cards). A tap on it stays a tap.
  startsOn?: string
}

const HOLD_MS = 260
const SLOP = 10

export function holdToDrag(card: HTMLElement, opts: HoldDragOptions): void {
  card.draggable = false
  card.classList.add('hold-drag-card')
  let timer = 0
  let start: { x: number; y: number } | null = null
  let lift: { ghost: HTMLElement; layer: HTMLElement; dx: number; dy: number; from: DOMRect; over: { el: HTMLElement; iso: string } | null } | null = null
  let swallowClick = false

  const clear = (): void => { window.clearTimeout(timer); timer = 0; start = null }
  const zones = (on: boolean): void => { for (const d of opts.days()) d.classList.toggle('hold-drop-zone', on) }
  const place = (x: number, y: number): void => {
    if (!lift) return
    // Above the finger, slightly smaller, so the day being aimed at stays in sight.
    const wide = lift.from.width * 0.9
    const left = Math.max(8, Math.min(x - lift.dx * 0.9, window.innerWidth - wide - 8))
    lift.ghost.style.transform = `translate(${left}px, ${y - lift.dy}px) scale(.9) rotate(-1.5deg)`
    const over = opts.dayAt(x, y)
    if (over?.el !== lift.over?.el) {
      lift.over?.el.classList.remove('hold-drop-over')
      over?.el.classList.add('hold-drop-over')
      if (over) tick()
      lift.over = over
    }
  }
  const end = (drop: boolean): void => {
    if (!lift) return
    const done = lift
    lift = null
    zones(false)
    done.over?.el.classList.remove('hold-drop-over')
    card.classList.remove('hold-drag-lifted')
    if (drop && done.over) {
      tick()
      done.layer.remove()
      opts.drop(done.over.iso)
      return
    }
    swallowClick = true
    window.setTimeout(() => { swallowClick = false }, 400)
    // Back to where it came from, then gone.
    done.ghost.style.transition = 'transform 180ms ease-out, opacity 180ms ease-out'
    done.ghost.style.transform = `translate(${done.from.left}px, ${done.from.top}px) scale(1)`
    done.ghost.style.opacity = '0.4'
    window.setTimeout(() => done.layer.remove(), 190)
  }

  card.addEventListener('touchstart', (e) => {
    const on = e.target as Element | null
    if (e.touches.length !== 1 || (on?.closest('.row-menu, a, button, input, select') && !(opts.startsOn && on?.closest(opts.startsOn)))) return
    const t = e.touches[0]!
    start = { x: t.clientX, y: t.clientY }
    timer = window.setTimeout(() => {
      if (!start) return
      const from = card.getBoundingClientRect()
      // The copy keeps the card's own look: it sits in the same kind of box the card sits in.
      const layer = document.createElement('div')
      layer.className = 'diary-new hold-drag-layer'
      const box = document.createElement('div')
      box.className = 'diary-place hold-drag-box'
      const ghost = card.cloneNode(true) as HTMLElement
      ghost.classList.add('hold-drag-ghost')
      ghost.removeAttribute('id')
      ghost.style.width = `${from.width}px`
      box.append(ghost); layer.append(box); document.body.append(layer)
      // It rises from where the card was to just above the finger.
      lift = { ghost, layer, dx: start.x - from.left, dy: from.height * 0.9 + 18, from, over: null }
      ghost.style.transformOrigin = '0 0'
      ghost.style.transform = `translate(${from.left}px, ${from.top}px)`
      card.classList.add('hold-drag-lifted')
      zones(true)
      tick()
      const at = start
      start = null
      requestAnimationFrame(() => {
        if (!lift || lift.ghost !== ghost) return
        ghost.style.transition = 'transform 140ms ease-out'
        place(at.x, at.y)
        window.setTimeout(() => { ghost.style.transition = '' }, 150)
      })
    }, HOLD_MS)
  }, { passive: true })

  card.addEventListener('touchmove', (e) => {
    const t = e.touches[0]
    if (!t) return
    if (lift) { e.preventDefault(); place(t.clientX, t.clientY); return }
    if (start && Math.hypot(t.clientX - start.x, t.clientY - start.y) > SLOP) clear()
  }, { passive: false })

  card.addEventListener('touchend', (e) => { if (lift) { e.preventDefault(); end(true) } else clear() })
  card.addEventListener('touchcancel', () => { if (lift) end(false); else clear() })
  // The phone's own menu for a held item stays away while a card is being held.
  card.addEventListener('contextmenu', (e) => { if (lift || timer) e.preventDefault() })
  // A drag is not a tap: the tap that follows letting go must not also pick or un-pick the card.
  card.addEventListener('click', (e) => { if (swallowClick) { e.preventDefault(); e.stopImmediatePropagation(); swallowClick = false } }, true)
}
